/**
 * Demo In-Memory Mutable Store
 * 세션 내에서 CRUD 상태를 유지합니다. 새로고침하면 초기 mock 데이터로 리셋됩니다.
 */
import { DocInfo } from "../types/files";
import { Workgroup } from "../types/workgroup";
import { meetingDTO } from "../types/meeting";
import {
  allDemoDocs,
  demoDocsByWorkgroup as _initialDocsByWorkgroup,
  demoTrashDocs,
  demofavoriteDocs as _initialFavDocs,
} from "./mockData/documents";
import { demoWorkgroups } from "./mockData/workgroups";
import { demoMeetings } from "./mockData/meetings";

// ── Documents ─────────────────────────────────────────────────────────────────
export let store_docs: DocInfo[] = allDemoDocs.map((d) => ({ ...d }));
export let store_trash: DocInfo[] = demoTrashDocs.map((d) => ({ ...d }));

export const getDocsByWorkgroup = (workgroupId: string): DocInfo[] =>
  store_docs.filter(
    (d) => (d as any).workgroup?.workgroupId === workgroupId && d.status === "N"
  );

export const getAllActiveDocs = (): DocInfo[] =>
  store_docs.filter((d) => d.status === "N");

export const getFavoriteDocs = (): DocInfo[] =>
  store_docs.filter((d) => d.favoritDate > 0 && d.status === "N");

export const findDoc = (syncId: string): DocInfo | undefined =>
  store_docs.find((d) => d.syncId === syncId) ??
  store_trash.find((d) => d.syncId === syncId);

/** 문서 삭제: _docs에서 제거하고 trash로 이동 */
export const deleteDoc = (syncIds: string[]): void => {
  const now = Date.now();
  syncIds.forEach((syncId) => {
    const idx = store_docs.findIndex((d) => d.syncId === syncId);
    if (idx !== -1) {
      const doc = store_docs.splice(idx, 1)[0];
      store_trash.push({ ...doc, status: "D", deleteDate: now } as any);
    }
  });
};

/** 문서 복원: trash에서 꺼내 _docs로 이동 */
export const restoreDoc = (syncIds: string[]): void => {
  syncIds.forEach((syncId) => {
    const idx = store_trash.findIndex((d) => d.syncId === syncId);
    if (idx !== -1) {
      const doc = store_trash.splice(idx, 1)[0];
      store_docs.push({ ...doc, status: "N", deleteDate: undefined } as any);
    }
  });
};

/** 영구 삭제: trash에서 완전 제거 */
export const permanentDeleteDoc = (syncIds: string[]): void => {
  syncIds.forEach((syncId) => {
    const idx = store_trash.findIndex((d) => d.syncId === syncId);
    if (idx !== -1) store_trash.splice(idx, 1);
  });
};

/** 문서 이름 변경 */
export const renameDoc = (syncId: string, newName: string): void => {
  const doc = store_docs.find((d) => d.syncId === syncId);
  if (doc) doc.contentName = newName;
};

/** 즐겨찾기 토글 */
export const toggleDocFavorite = (
  syncId: string,
  flag: boolean | "Y" | "N"
): void => {
  const doc = store_docs.find((d) => d.syncId === syncId);
  if (!doc) return;
  const isFav = typeof flag === "boolean" ? flag : flag === "Y";
  doc.favoritDate = isFav ? Date.now() : 0;
};

// ── Workgroups ────────────────────────────────────────────────────────────────
export let store_workgroups: Workgroup[] = demoWorkgroups.map((wg) => ({
  ...wg,
}));

export const getWorkgroupMap = (): Record<string, Workgroup> =>
  Object.fromEntries(store_workgroups.map((wg) => [wg.workgroupId, wg]));

export const addWorkgroup = (wg: Workgroup): void => {
  store_workgroups.push(wg);
};

export const deleteWorkgroup = (workgroupId: string): void => {
  const idx = store_workgroups.findIndex(
    (wg) => wg.workgroupId === workgroupId
  );
  if (idx !== -1) store_workgroups.splice(idx, 1);
};

export const renameWorkgroup = (workgroupId: string, newName: string): void => {
  const wg = store_workgroups.find((w) => w.workgroupId === workgroupId);
  if (wg) {
    wg.workgroupName = newName;
    // docs의 workgroup.workgroupName도 업데이트
    store_docs.forEach((d) => {
      if ((d as any).workgroup?.workgroupId === workgroupId) {
        (d as any).workgroup.workgroupName = newName;
      }
    });
  }
};

export const toggleWorkgroupFavorite = (
  workgroupId: string,
  favorite: string
): void => {
  const wg = store_workgroups.find((w) => w.workgroupId === workgroupId);
  if (wg) wg.favorite = favorite;
};

// ── Link Policies ─────────────────────────────────────────────────────────────
interface FileLinkPolicyData {
  authType: string;
  password: string | null;
  urlAction: string;
  allowDays: number;
  allowDaysTimeZone: number;
}

const store_linkPolicies: Record<string, FileLinkPolicyData> = {};

const defaultLinkPolicy = (): FileLinkPolicyData => ({
  authType: "NONE",
  password: null,
  urlAction: "WEBUSING",
  allowDays: 30,
  allowDaysTimeZone: 9,
});

export const getLinkPolicy = (syncId: string): FileLinkPolicyData =>
  store_linkPolicies[syncId] ?? defaultLinkPolicy();

export const setLinkPolicy = (
  syncId: string,
  policy: Partial<FileLinkPolicyData>
): FileLinkPolicyData => {
  store_linkPolicies[syncId] = { ...getLinkPolicy(syncId), ...policy };
  return store_linkPolicies[syncId];
};

// ── Document Permissions (권한) ───────────────────────────────────────────────
const store_docPermissions: Record<string, any[]> = {};

export const setDocPermissions = (syncId: string, policyList: any[]): void => {
  store_docPermissions[syncId] = policyList;
};

export const getDocPermissions = (syncId: string): any[] | null =>
  store_docPermissions[syncId] ?? null;

// ── Comments (의견) ───────────────────────────────────────────────────────────
export interface CommentItem {
  memo: string;
  revisionNo: number;
  syncId: string;
  regDate: number;
  userId: string;
  userName: string;
  userType: number;
}

const store_comments: CommentItem[] = [];

export const addComment = (
  syncId: string,
  memo: string,
  revisionNo: number,
  userId: string,
  userName: string,
  userType: number
): CommentItem => {
  const item: CommentItem = {
    memo,
    revisionNo,
    syncId,
    regDate: Date.now(),
    userId,
    userName,
    userType,
  };
  store_comments.push(item);
  return item;
};

export const getComments = (syncId: string): CommentItem[] =>
  store_comments.filter((c) => c.syncId === syncId);

// ── Read Requests (열람 요청) ──────────────────────────────────────────────────
export interface ReadRequestItem {
  syncId: string;
  requestId: string;
  requestUser: string;
  targetUserList: string[];
  requestTime: number;
  requestInterval: number;
  reRequestTime: number;
  comment: string;
  timezoneOffset: number;
}

const store_readRequests: ReadRequestItem[] = [];

export const addReadRequest = (req: Omit<ReadRequestItem, "requestId" | "reRequestTime">): ReadRequestItem => {
  const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const reRequestTime = req.requestTime + req.requestInterval * 86400000;
  const item: ReadRequestItem = { ...req, requestId, reRequestTime };
  store_readRequests.push(item);
  return item;
};

export const getReadRequests = (syncId: string): ReadRequestItem[] =>
  store_readRequests.filter((r) => r.syncId === syncId);

const deleted_readRequestIds = new Set<string>();

export const deleteReadRequest = (syncId: string, requestId: string): void => {
  const idx = store_readRequests.findIndex((r) => r.syncId === syncId && r.requestId === requestId);
  if (idx !== -1) store_readRequests.splice(idx, 1);
  deleted_readRequestIds.add(requestId);
};

export const isReadRequestDeleted = (requestId: string): boolean =>
  deleted_readRequestIds.has(requestId);

export const incrementDocSignCount = (syncId: string): void => {
  const doc = store_docs.find((d) => d.syncId === syncId);
  if (doc) (doc as any).signCount = ((doc as any).signCount ?? 0) + 1;
};

// ── Folders ───────────────────────────────────────────────────────────────────
interface FolderNode { id: string; parentId: string; name: string }

export const store_folderTree: Record<string, FolderNode[]> = {
  "wg-product-design": [
    { id: "wg-product-design", parentId: "ECO_WORKGROUP", name: "Product Design" },
    { id: "fd-pd-01", parentId: "wg-product-design", name: "Wireframes" },
    { id: "fd-pd-02", parentId: "wg-product-design", name: "Prototypes" },
    { id: "fd-pd-03", parentId: "wg-product-design", name: "Assets" },
    { id: "fd-pd-0101", parentId: "fd-pd-01", name: "Mobile" },
    { id: "fd-pd-0102", parentId: "fd-pd-01", name: "Web" },
  ],
  "wg-engineering-core": [
    { id: "wg-engineering-core", parentId: "ECO_WORKGROUP", name: "Engineering Core" },
    { id: "fd-ec-01", parentId: "wg-engineering-core", name: "Architecture" },
    { id: "fd-ec-02", parentId: "wg-engineering-core", name: "API Specs" },
    { id: "fd-ec-0101", parentId: "fd-ec-01", name: "Diagrams" },
  ],
  "wg-marketing-campaign": [
    { id: "wg-marketing-campaign", parentId: "ECO_WORKGROUP", name: "Marketing Campaign" },
    { id: "fd-mc-01", parentId: "wg-marketing-campaign", name: "Campaign A" },
    { id: "fd-mc-02", parentId: "wg-marketing-campaign", name: "Brand Assets" },
    { id: "fd-mc-03", parentId: "wg-marketing-campaign", name: "Reports" },
  ],
  "wg-security-compliance": [
    { id: "wg-security-compliance", parentId: "ECO_WORKGROUP", name: "Security & Compliance" },
    { id: "fd-sc-01", parentId: "wg-security-compliance", name: "Policies" },
    { id: "fd-sc-02", parentId: "wg-security-compliance", name: "Audit Logs" },
  ],
  "wg-executive-projects": [
    { id: "wg-executive-projects", parentId: "ECO_WORKGROUP", name: "Executive Projects" },
    { id: "fd-ep-01", parentId: "wg-executive-projects", name: "Strategy" },
    { id: "fd-ep-02", parentId: "wg-executive-projects", name: "Roadmaps" },
  ],
};

export const addFolder = (workgroupId: string, node: FolderNode): void => {
  if (!store_folderTree[workgroupId]) {
    store_folderTree[workgroupId] = [
      { id: workgroupId, parentId: "ECO_WORKGROUP", name: workgroupId },
    ];
  }
  store_folderTree[workgroupId].push(node);
};

export const updateFolder = (workgroupId: string, folderId: string, folderName: string): FolderNode | null => {
  const node = store_folderTree[workgroupId]?.find((f) => f.id === folderId);
  if (node && folderName) node.name = folderName;
  return node ?? null;
};

// ── Doc Folder 위치 ────────────────────────────────────────────────────────────
// syncId → folderId 매핑 (없으면 워크그룹 루트)
const store_docFolders: Record<string, string> = {};

// 초기 데이터: 각 워크그룹의 docs 중 약 2/3을 서브 폴더에 분배, 나머지는 root에
(function initDocFolders() {
  const wgFolders: Record<string, string[]> = {};
  for (const [wgId, nodes] of Object.entries(store_folderTree)) {
    const subFolders = nodes.filter((n) => n.parentId !== "ECO_WORKGROUP").map((n) => n.id);
    if (subFolders.length > 0) wgFolders[wgId] = subFolders;
  }
  store_docs.forEach((doc) => {
    const wgId = (doc as any).workgroup?.workgroupId;
    const folders = wgFolders[wgId];
    if (!folders || folders.length === 0) return;
    let hash = 0;
    for (let i = 0; i < doc.syncId.length; i++) {
      hash = (hash * 31 + doc.syncId.charCodeAt(i)) >>> 0;
    }
    // hash % 3 === 0 이면 폴더 배정 (약 1/3), 나머지는 root에 남김
    if (hash % 3 === 0) {
      store_docFolders[doc.syncId] = folders[hash % folders.length];
    }
  });
})();

export const getDocFolder = (syncId: string): string | undefined =>
  store_docFolders[syncId];

export const moveDocsToFolder = (syncIds: string[], folderId: string): void => {
  syncIds.forEach((syncId) => {
    store_docFolders[syncId] = folderId;
  });
};

// ── Meetings ──────────────────────────────────────────────────────────────────
export let store_meetings: meetingDTO[] = demoMeetings.map((m) => ({ ...m }));

export const toggleMeetingFavorite = (
  meetingId: string,
  favorite?: string
): void => {
  const m = store_meetings.find((x) => x.meetingId === meetingId);
  if (!m) return;
  m.favorite = favorite ?? (m.favorite === "Y" ? "N" : "Y");
};

export const addMeeting = (meeting: meetingDTO): void => {
  store_meetings.push(meeting);
};

export const deleteMeeting = (meetingId: string): void => {
  const idx = store_meetings.findIndex((m) => m.meetingId === meetingId);
  if (idx !== -1) store_meetings.splice(idx, 1);
};

export const updateMeeting = (meetingId: string, patch: Partial<meetingDTO>): meetingDTO | null => {
  const m = store_meetings.find((x) => x.meetingId === meetingId);
  if (!m) return null;
  Object.assign(m, patch);
  return m;
};
